/**
 * <spivex-consent-banner>: the Spivex cookie notice (sections/spivex-cookie-banner.liquid).
 *
 * Uses only Shopify's Customer Privacy API (consent-tracking-api 0.2). Consent is recorded with setTrackingConsent
 * after a click, never automatically, and Shopify cookies are never read or written directly. Withdrawing a purpose
 * removes what Google and Spivex stored for it on this domain.
 * The card appears while no choice has been made yet: for every visitor (data-show-everywhere, default) or only
 * where Shopify's privacy settings ask for a banner (shouldShowBanner). Links to "#cookie-einstellungen" and
 * elements with data-spivex-consent-open (the permanent footer link) show the choices again, preset with the
 * current consent.
 */
const PURPOSES = /** @type {const} */ (['analytics', 'marketing', 'preferences']);
const OPEN_HASH = '#cookie-einstellungen';
// What each purpose leaves on this domain. Cookies on Google's own domains (IDE, GCL_AW_P) cannot be removed from here.
const STORED = {
  analytics: { cookies: /^_ga(_|$)/, local: ['spivex-ab'] },
  marketing: { cookies: /^(_gcl_|_gac_|_fbp$|_fbc$)/, local: ['_gcl_ls'], session: /^spivex_ads_/ },
  preferences: { local: ['viewedProducts'] },
};

/**
 * Removes what the purposes that are not allowed stored on this domain.
 * @param {Record<string, boolean>} allowed
 */
function forget(allowed) {
  for (const [purpose, { cookies, local = [], session }] of Object.entries(STORED)) {
    if (allowed[purpose]) continue;
    for (const pair of document.cookie.split('; ')) {
      const name = pair.split('=')[0];
      if (!cookies?.test(name)) continue;
      // Google sets its cookies for the whole domain, so clear that variant too.
      document.cookie = `${name}=; Max-Age=0; path=/; domain=.${location.hostname}`;
      document.cookie = `${name}=; Max-Age=0; path=/`;
    }
    try {
      for (const key of local) localStorage.removeItem(key);
      for (const key of Object.keys(sessionStorage)) if (session?.test(key)) sessionStorage.removeItem(key);
    } catch {
      // Blocked storage holds nothing to remove.
    }
  }
}

class SpivexConsentBanner extends HTMLElement {
  /** @type {HTMLElement | null} */
  #choices = null;

  connectedCallback() {
    this.#choices = this.querySelector('[data-spivex-consent-choices]');
    this.addEventListener('click', this.#onClick);
    document.addEventListener('click', this.#onOpenLink);
    if (this.dataset.designMode === 'true') return;

    window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.2' }], (error) => {
      if (error) return;
      // Clears leftovers from an earlier withdrawal. Undecided visitors outside the banner regions keep their A/B variant.
      const consent = window.Shopify?.customerPrivacy?.currentVisitorConsent?.() ?? {};
      forget({
        analytics: consent.analytics !== 'no',
        marketing: consent.marketing !== 'no',
        preferences: consent.preferences === 'yes',
      });
      if (location.hash === OPEN_HASH) {
        this.open(true);
      } else if (this.#needsChoice()) {
        this.open(false);
      }
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('click', this.#onOpenLink);
  }

  /**
   * Shows the card.
   * @param {boolean} withChoices - Also show the per-purpose choices, preset with the current consent
   */
  open(withChoices) {
    this.hidden = false;
    this.#showChoices(withChoices);
  }

  #needsChoice() {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy?.currentVisitorConsent) return false;
    if (this.dataset.showEverywhere !== 'true' && !privacy.shouldShowBanner?.()) return false;
    const consent = privacy.currentVisitorConsent?.() ?? {};
    return PURPOSES.every((purpose) => !consent[purpose]);
  }

  /** @param {boolean} visible */
  #showChoices(visible) {
    if (!this.#choices) return;
    this.#choices.hidden = !visible;
    this.querySelector('[data-spivex-consent="save"]')?.toggleAttribute('hidden', !visible);
    this.querySelector('[data-spivex-consent="settings"]')?.setAttribute('aria-expanded', String(visible));
    if (!visible) return;

    const consent = window.Shopify?.customerPrivacy?.currentVisitorConsent?.() ?? {};
    for (const purpose of PURPOSES) {
      const input = /** @type {HTMLInputElement | null} */ (this.#choices.querySelector(`input[name="${purpose}"]`));
      if (input) input.checked = consent[purpose] === 'yes';
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-spivex-consent]') : null
    );
    if (!button) return;

    switch (button.dataset.spivexConsent) {
      case 'accept':
        this.#save({ analytics: true, marketing: true, preferences: true });
        break;
      case 'decline':
        this.#save({ analytics: false, marketing: false, preferences: false });
        break;
      case 'settings':
        this.#showChoices(Boolean(this.#choices?.hidden));
        break;
      case 'save': {
        /** @type {Record<string, boolean>} */
        const choice = {};
        for (const purpose of PURPOSES) {
          const input = /** @type {HTMLInputElement | null} */ (this.querySelector(`input[name="${purpose}"]`));
          choice[purpose] = Boolean(input?.checked);
        }
        this.#save(choice);
        break;
      }
    }
  };

  /** @param {MouseEvent} event */
  #onOpenLink = (event) => {
    const link = event.target instanceof Element ? event.target.closest(`a[href$="${OPEN_HASH}"], [data-spivex-consent-open]`) : null;
    if (!link || !window.Shopify?.customerPrivacy) return;
    event.preventDefault();
    this.open(true);
    this.querySelector('input[name="analytics"]')?.focus();
  };

  /** @param {Record<string, boolean>} choice */
  #save(choice) {
    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy?.setTrackingConsent) {
      this.hidden = true;
      return;
    }
    // Tell Google before saving: saving makes the "Google & YouTube" app queue its own page start, and GTM runs its base
    // tags at that point. Without this they see the old "denied", block, and the click ID is never stored.
    const ads = choice.marketing ? 'granted' : 'denied';
    window.gtag?.('consent', 'update', { ad_storage: ads, ad_user_data: ads, ad_personalization: ads });
    privacy.setTrackingConsent(choice, () => {
      forget(choice);
      this.hidden = true;
      if (location.hash === OPEN_HASH) history.replaceState(null, '', location.pathname + location.search);
    });
  }
}

if (!customElements.get('spivex-consent-banner')) {
  customElements.define('spivex-consent-banner', SpivexConsentBanner);
}
